import { useState, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, Filter, List, LayoutGrid } from 'lucide-react';
import { ProductList, ProductListView } from '../components/products';
import { getProducts, getCategories } from '../services/productService';
import type { ProductWithMedia } from '../types/products';
import { PRODUCT_TYPE_LABELS } from '../types/products';
import { logger } from '../lib/logger';

const PAGE_SIZE = 12;

type ViewMode = 'list' | 'grid';

export function Products() {
  const [products, setProducts] = useState<ProductWithMedia[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('products-view-mode');
    return (saved as ViewMode) || 'list';
  });

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('products-view-mode', mode);
  };

  const loadProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getProducts(
        {
          search: search || undefined,
          category: categoryFilter || undefined,
          productType: typeFilter || undefined,
        },
        { page: currentPage, pageSize: PAGE_SIZE }
      );
      setProducts(result.products);
      setTotalCount(result.totalCount);
    } catch (error) {
      logger.error('Failed to load products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [search, categoryFilter, typeFilter, currentPage]);

  const loadCategories = useCallback(async () => {
    try {
      const cats = await getCategories();
      setCategories(cats);
    } catch (error) {
      logger.error('Failed to load categories:', error);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, categoryFilter, typeFilter]);

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setTypeFilter('');
  };

  const hasActiveFilters = search || categoryFilter || typeFilter;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] dark:text-white mb-1">Produkte</h1>
          <p className="text-[#6B6B6B] dark:text-slate-400">
            {totalCount} Produkt{totalCount !== 1 ? 'e' : ''} im Katalog
          </p>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-700 p-1 rounded-xl">
          <button
            onClick={() => handleViewModeChange('list')}
            className={`
              flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all
              ${viewMode === 'list'
                ? 'bg-white dark:bg-slate-600 text-[#1A1A1A] dark:text-white shadow-sm'
                : 'text-[#6B6B6B] dark:text-slate-400 hover:text-[#1A1A1A] dark:hover:text-white'
              }
            `}
          >
            <List className="w-4 h-4" />
            <span className="hidden sm:inline">Liste</span>
          </button>
          <button
            onClick={() => handleViewModeChange('grid')}
            className={`
              flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all
              ${viewMode === 'grid'
                ? 'bg-white dark:bg-slate-600 text-[#1A1A1A] dark:text-white shadow-sm'
                : 'text-[#6B6B6B] dark:text-slate-400 hover:text-[#1A1A1A] dark:hover:text-white'
              }
            `}
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="hidden sm:inline">Kacheln</span>
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nach Produktname oder Artikelnummer suchen..."
              className="w-full pl-12 pr-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-800 dark:text-white"
            />
          </div>

          {/* Filter Toggle (Mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors dark:text-white"
          >
            <Filter className="w-5 h-5" />
            <span>Filter</span>
            {hasActiveFilters && (
              <span className="w-2 h-2 bg-primary rounded-full" />
            )}
          </button>

          {/* Desktop Filters */}
          <div className="hidden lg:flex gap-3">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-800 dark:text-white min-w-[180px]"
            >
              <option value="">Alle Kategorien</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-800 dark:text-white min-w-[150px]"
            >
              <option value="">Alle Typen</option>
              {Object.entries(PRODUCT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2.5 text-primary hover:bg-primary/10 rounded-xl transition-colors font-medium"
              >
                Zurücksetzen
              </button>
            )}
          </div>
        </div>

        {/* Mobile Filters */}
        {showFilters && (
          <div className="lg:hidden mt-4 pt-4 border-t border-gray-100 dark:border-slate-700 space-y-3">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-800 dark:text-white"
            >
              <option value="">Alle Kategorien</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-800 dark:text-white"
            >
              <option value="">Alle Typen</option>
              {Object.entries(PRODUCT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2.5 text-primary hover:bg-primary/10 rounded-xl transition-colors font-medium"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>
        )}
      </div>

      {/* Product List/Grid */}
      {viewMode === 'list' ? (
        <ProductListView products={products} isLoading={isLoading} />
      ) : (
        <ProductList products={products} isLoading={isLoading} />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-[#6B6B6B] dark:text-slate-400 text-center sm:text-left">
            Seite {currentPage} von {totalPages}
          </p>
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 border border-gray-200 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:text-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`
                      w-10 h-10 rounded-lg font-medium transition-colors
                      ${currentPage === pageNum
                        ? 'bg-primary text-white'
                        : 'hover:bg-gray-100 dark:hover:bg-slate-700 text-[#6B6B6B] dark:text-slate-400'
                      }
                    `}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 border border-gray-200 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:text-white"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
